import { CreateClaimProvider, CreateClaimProviderType, SupportProvider } from '../../../../api';
import { Field, Formik, FormikProps } from 'formik';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { ClaimEditorStore } from '../../../../stores/claim-editor';
import * as S from './additional-providers-form.styles';
import * as CS from '../section.styles';
import { BaseButton } from '../../../base';
import { InputField } from '../../../base/form';
import { ProviderAutocomplete } from './provider-autocomplete';
import { validateProviderFormValues } from '../../../../stores/claim-editor/claim-editor-rules';
import { fieldDomId } from '../../../../stores/claim-editor/claim-editor-sections';
import { useClaimEditorRevealFieldErrors } from '../use-claim-editor-form-validation';

interface ProviderFormValues {
    providerLookup?: string;
    organization: string;
    first: string;
    last: string;
    suffix?: string;
    npi: string;
    phone: string;
    fax: string;
    providerId?: string;
}

const initialValues: ProviderFormValues = {
    organization: '',
    first: '',
    last: '',
    suffix: '',
    npi: '',
    phone: '',
    fax: '',
    providerId: undefined,
};

const hasProviderIdentityChanged = (
    values: ProviderFormValues,
    selectedProviderValues?: ProviderFormValues | null,
): boolean => {
    if (!selectedProviderValues) return false;
    return (
        values.organization !== selectedProviderValues.organization ||
        values.first !== selectedProviderValues.first ||
        values.last !== selectedProviderValues.last ||
        values.suffix !== selectedProviderValues.suffix ||
        values.npi !== selectedProviderValues.npi ||
        values.phone !== selectedProviderValues.phone ||
        values.fax !== selectedProviderValues.fax
    );
};

interface IAdditionalProvidersFormProps {
    claimEditorStore: ClaimEditorStore;
}

export const AdditionalProvidersForm: React.FunctionComponent<IAdditionalProvidersFormProps> = observer(props => {
    const { claimEditorStore } = props;

    const handleAddProvider = useCallback((type: CreateClaimProviderType) => {
        const currentProviders = claimEditorStore?.providers || [];
        const newProvider: CreateClaimProvider = {
            first: undefined,
            middle: undefined,
            last: undefined,
            suffix: undefined,
            npi: undefined,
            phone: undefined,
            fax: undefined,
            providerId: undefined,
            type,
        };
        claimEditorStore?.updateProviders([...currentProviders, newProvider]);
    }, [claimEditorStore]);

    const handleDeleteProvider = useCallback((index: number) => {
        const currentProviders = claimEditorStore?.providers || [];
        const updatedProviders = currentProviders.filter((_, i) => i !== index);
        claimEditorStore?.updateProviders(updatedProviders);
    }, [claimEditorStore]);

    const handleUpdateProvider = useCallback((index: number, provider: CreateClaimProvider) => {
        const currentProviders = claimEditorStore?.providers || [];
        const updatedProviders = [...currentProviders];
        updatedProviders[index] = provider;
        claimEditorStore?.updateProviders(updatedProviders);
    }, [claimEditorStore]);

    const providers = claimEditorStore?.providers || [];
    const hasRendering = providers.some(p => p.type === CreateClaimProviderType.RENDERING);
    const hasReferring = providers.some(p => p.type === CreateClaimProviderType.REFERRING);
    const hasSupervising = providers.some(p => p.type === CreateClaimProviderType.SUPERVISING);

    return (
        <S.FormContainer>
            <CS.ButtonContainer>
                <BaseButton 
                    variant="primary" 
                    onClick={() => handleAddProvider(CreateClaimProviderType.RENDERING)} 
                    disabled={hasRendering}
                >
                    + Add Rendering
                </BaseButton>
                <BaseButton 
                    variant="primary" 
                    onClick={() => handleAddProvider(CreateClaimProviderType.REFERRING)} 
                    disabled={hasReferring}
                >
                    + Add Referring
                </BaseButton>
                <BaseButton 
                    variant="primary" 
                    onClick={() => handleAddProvider(CreateClaimProviderType.SUPERVISING)} 
                    disabled={hasSupervising}
                >
                    + Add Supervising
                </BaseButton>
            </CS.ButtonContainer>

            {providers.map((provider, index) => (
                <ProviderSection
                    key={`${provider.type}-${index}`}
                    providerType={provider.type}
                    title={provider.type}
                    existingProvider={provider}
                    index={index}
                    onDelete={handleDeleteProvider}
                    onUpdateProvider={handleUpdateProvider}
                    claimEditorStore={claimEditorStore}
                />
            ))}
        </S.FormContainer>
    );
});

interface ProviderSectionProps {
    providerType: CreateClaimProviderType;
    title: string;
    existingProvider: CreateClaimProvider;
    index: number;
    onDelete: (index: number) => void;
    onUpdateProvider: (index: number, provider: CreateClaimProvider) => void;
    claimEditorStore: ClaimEditorStore;
}

const ProviderSection: React.FunctionComponent<ProviderSectionProps> = observer(
    ({ providerType, title, index, onDelete, onUpdateProvider, existingProvider, claimEditorStore }) => {
        const validationSignal = claimEditorStore.validationSignal;
        const validationEvaluated = claimEditorStore.validationEvaluated;
        const formikRef = useRef<FormikProps<ProviderFormValues> | null>(null);
        const isMountedRef = useRef(false);
        const selectedProviderValuesRef = useRef<ProviderFormValues | null>(null);

        const getInitialValues = useCallback((): ProviderFormValues => {
            if (existingProvider) {
                const displayName =
                    [existingProvider.first, existingProvider.last].filter(Boolean).join(' ') ||
                    existingProvider.organization ||
                    '';
                return {
                    providerLookup: displayName,
                    organization: existingProvider.organization || '',
                    first: existingProvider.first || '',
                    last: existingProvider.last || '',
                    suffix: existingProvider.suffix || '',
                    npi: existingProvider.npi || '',
                    phone: existingProvider.phone || '',
                    fax: existingProvider.fax || '',
                    providerId: (existingProvider.providerId as any) || undefined,
                };
            }
            return { ...initialValues, providerLookup: '' };
        }, [existingProvider]);

        const convertToProvider = (values: ProviderFormValues): CreateClaimProvider => {
            return {
                organization: values.organization || undefined,
                first: values.first || undefined,
                middle: existingProvider.middle || undefined,
                last: values.last || undefined,
                suffix: values.suffix?.trim() ? values.suffix.trim() : undefined,
                npi: values.npi || undefined,
                phone: values.phone || undefined,
                fax: values.fax || undefined,
                providerId: (values.providerId as any) || undefined,
                type: providerType,
            };
        };

        const debouncedSave = useCallback(
            _.debounce(() => {
                const latestValues = formikRef.current?.values;
                if (!latestValues) return;
                const providerData = convertToProvider(latestValues);
                onUpdateProvider(index, providerData);
            }, 500),
            [onUpdateProvider, index],
        );

        const handleProviderSelect = useCallback(
            (provider: SupportProvider, formik: FormikProps<ProviderFormValues>) => {
                const displayName =
                    [provider.first, provider.last].filter(Boolean).join(' ') || provider.organization || '';
                const newValues: ProviderFormValues = {
                    providerLookup: displayName,
                    organization: provider.organization || '',
                    first: provider.first || '',
                    last: provider.last || '',
                    suffix: provider.suffix || '',
                    npi: provider.npi || '',
                    phone: provider.phone || '',
                    fax: provider.fax || '',
                    providerId: (provider.id as any) || undefined,
                };
                selectedProviderValuesRef.current = newValues;
                formik.setValues(newValues);
            },
            [],
        );

        const handleProviderClear = useCallback((formik: FormikProps<ProviderFormValues>) => {
            formik.setFieldValue('providerId', undefined);
            formik.setFieldValue('providerLookup', '');
        }, []);

        useEffect(() => {
            return () => {
                debouncedSave.cancel();
            };
        }, [debouncedSave]);

        return (
            <CS.SectionContainer>
                <CS.SectionHeader>
                    <CS.SectionTitle>{title}</CS.SectionTitle>
                    <BaseButton variant="warning" onClick={() => onDelete(index)}>✕ Delete {title} Provider</BaseButton>
                </CS.SectionHeader>

                <Formik
                    innerRef={formikRef as any}
                    initialValues={getInitialValues()}
                    validate={values =>
                        validateProviderFormValues(values, {
                            isProviderIdActive: claimEditorStore.isProviderIdActive,
                        })
                    }
                    onSubmit={() => {
                        // No-op, we auto-save
                    }}
                    enableReinitialize
                    validateOnMount={false}
                    validateOnChange={true}
                    validateOnBlur={false}
                >
                    {formik => {
                        useEffect(() => {
                            if (!isMountedRef.current) {
                                isMountedRef.current = true;
                                return;
                            }
                            debouncedSave();
                        }, [formik.values, debouncedSave]);

                        useEffect(() => {
                            if (!selectedProviderValuesRef.current && formik.values.providerId) {
                                selectedProviderValuesRef.current = { ...formik.values };
                            }
                        }, [formik.values]);

                        useEffect(() => {
                            if (!formik.values.providerId) return;
                            if (!hasProviderIdentityChanged(formik.values, selectedProviderValuesRef.current)) return;
                            formik.setFieldValue('providerId', undefined, false);
                        }, [
                            formik.values.providerId,
                            formik.values.organization,
                            formik.values.first,
                            formik.values.last,
                            formik.values.suffix,
                            formik.values.npi,
                            formik.values.phone,
                            formik.values.fax,
                        ]);

                        useClaimEditorRevealFieldErrors(formik, validationSignal, validationEvaluated);

                        return (
                            <>
                                <S.FormGrid>
                                    <ProviderAutocomplete
                                        providerType={providerType}
                                        formik={formik}
                                        onSelect={(p: SupportProvider) => handleProviderSelect(p, formik)}
                                        onClear={() => handleProviderClear(formik)}
                                        claimEditorStore={claimEditorStore}
                                    />
                                    <S.FormField id={fieldDomId(`providers[${index}].organization`)}>
                                    <S.Label required={!formik.values.last.trim() || !formik.values.first.trim()}>Organization Name</S.Label>
                                        <Field
                                            name="organization"
                                            placeholder="Organization Name"
                                            component={InputField}
                                        />
                                    </S.FormField>

                                    <S.FormField id={fieldDomId(`providers[${index}].first`)}>
                                        <S.Label required={!formik.values.organization.trim()}>First Name</S.Label>
                                        <Field
                                            name="first"
                                            placeholder="First Name"
                                            component={InputField}
                                        />
                                    </S.FormField>

                                    <S.FormField id={fieldDomId(`providers[${index}].last`)}>
                                    <S.Label required={!formik.values.organization.trim()}>Last Name</S.Label>
                                        <Field
                                            name="last"
                                            placeholder="Last Name"
                                            component={InputField}
                                        />
                                    </S.FormField>

                                    <S.FormField id={fieldDomId(`providers[${index}].suffix`)}>
                                        <S.Label>Suffix</S.Label>
                                        <Field
                                            name="suffix"
                                            placeholder="Suffix"
                                            component={InputField}
                                            maxLength={10 as any}
                                        />
                                    </S.FormField>

                                    <S.FormField id={fieldDomId(`providers[${index}].npi`)}>
                                        <S.Label required>NPI</S.Label>
                                        <Field
                                            name="npi"
                                            placeholder="NPI"
                                            component={InputField}
                                            maxLength={10 as any}
                                        />
                                    </S.FormField>
                                    <S.FormField>
                                        <S.Label>Phone</S.Label>
                                        <Field
                                            name="phone"
                                            placeholder="Phone"
                                            component={InputField}
                                        />
                                    </S.FormField>
                                    <S.FormField>
                                        <S.Label>Fax</S.Label>
                                        <Field
                                            name="fax"
                                            placeholder="Fax"
                                            component={InputField}
                                        />
                                    </S.FormField>
                                </S.FormGrid>
                            </>
                        );
                    }}
                </Formik>
            </CS.SectionContainer>
        );
    },
);

